import { DOCUMENT, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { AzureLoginResult, AzureOAuthOptions } from '@libs/shared/models';
import { filter, tap } from 'rxjs';
import { AuthService } from './auth.service';
import { createUserToken, getUserAuthority, LL_DIRECTORY_USER_MSAL_SERVICE, OAuthConfigService } from './login';

// this is based on the reference implementation by ms
// https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-angular-v3-samples/angular-b2c-sample-app
@Injectable({
  providedIn: 'root',
})
export class AzureOAuthWebResultHandlerService {
  private readonly configService: OAuthConfigService = inject(OAuthConfigService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly msalBroadcastService: MsalBroadcastService = inject(MsalBroadcastService);
  private readonly document: Document = inject(DOCUMENT);

  private readonly llDirectoryUserMsal: MsalService = inject(LL_DIRECTORY_USER_MSAL_SERVICE);

  private readonly userMsal: MsalService = inject(MsalService);
  private readonly userMsalGuardConfig: MsalGuardConfiguration = (inject(MSAL_GUARD_CONFIG) as unknown as MsalGuardConfiguration);

  public constructor() {
    this.initLlDirectoryUserEvents();
    this.initUserMsalEvents();
    this.initBroadcastEvents();
    this.initBfCacheRestoreEvents();
  }

  private initBfCacheRestoreEvents(): void {
    const reloadOnBfCacheRestore = (): void => {
      this.document.defaultView?.location.reload();
    };

    this.userMsal.instance.addEventCallback(reloadOnBfCacheRestore, [EventType.RESTORE_FROM_BFCACHE]);
    this.llDirectoryUserMsal.instance.addEventCallback(reloadOnBfCacheRestore, [EventType.RESTORE_FROM_BFCACHE]);
  }

  private initBroadcastEvents(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        tap(status => void this.authService.setIsUpdating(status !== InteractionStatus.None)),
        filter(status => status === InteractionStatus.None),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        void this.checkAndSetActiveAccount(this.userMsal, this.configService.getUserAzureOAuthOptions().authorityHostname);
        void this.checkAndSetActiveAccount(this.llDirectoryUserMsal, this.configService.getLlDirectoryUserAzureOAuthOptions().authorityHostname);
      });

    this.msalBroadcastService.msalSubject$.pipe(takeUntilDestroyed()).subscribe((event) => {
      /* istanbul ignore else */
      if (event.error) {
        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (event.error.message.indexOf('AADB2C90118') > -1) {
          const config: AzureOAuthOptions = this.configService.getUserAzureOAuthOptions();
          const resetPasswordFlowRequest: RedirectRequest = {
            authority: getUserAuthority(config, config.resetPasswordPolicy),
            scopes: [],
          };

          if (this.userMsalGuardConfig.authRequest) {
            this.userMsal.loginRedirect({ ...this.userMsalGuardConfig.authRequest, ...resetPasswordFlowRequest });
          } else {
            this.userMsal.loginRedirect(resetPasswordFlowRequest);
          }
          // this error code is returned when the login or reset password was cancelled
        } else if (event.error.message.indexOf('AADB2C90091') === -1) {
          void this.authService.onLoginFailed();
        }
      }
    });
  }

  private initLlDirectoryUserEvents(): void {
    this.llDirectoryUserMsal.handleRedirectObservable().pipe(takeUntilDestroyed()).subscribe((res) => {
      if (!res) {
        return;
      }

      const result: AzureLoginResult = {
        accessToken: res.accessToken,
        expiresOn: res.expiresOn?.getTime().toString() ?? undefined,
        idToken: res.idToken,
        accountId: res.account.nativeAccountId,
        type: 'llDirectoryUser',
      };

      this.authService.loginLlDirectoryUser(createUserToken(result)).subscribe();
    });
  }

  private initUserMsalEvents(): void {
    this.userMsal.handleRedirectObservable().pipe(takeUntilDestroyed()).subscribe((res) => {
      if (!res) {
        return;
      }

      const result: AzureLoginResult = {
        type: 'user',
        accessToken: res.accessToken,
        expiresOn: res.expiresOn?.getTime().toString() ?? undefined,
        idToken: res.idToken,
        accountId: res.account.nativeAccountId,
      };

      this.authService.login(createUserToken(result)).subscribe();
    });
  }

  /**
   * Both MSAL instances share one localStorage and getAllAccounts() is not partitioned by clientId.
   * Only accounts of the instance's own authority may become the active account, otherwise the next
   * login fails with an authority_mismatch error.
   */
  private async checkAndSetActiveAccount(msal: MsalService, authorityHostname: string): Promise<void> {
    await msal.instance.initialize();

    const activeAccount: AccountInfo | null = msal.instance.getActiveAccount();

    if (activeAccount?.environment === authorityHostname) {
      return;
    }

    const accounts: AccountInfo[] = msal.instance.getAllAccounts().filter((account: AccountInfo) => account.environment === authorityHostname);

    if (accounts.length > 0) {
      msal.instance.setActiveAccount(accounts[0]);

      return;
    }

    if (activeAccount) {
      // Stale account from another authority because no replacement available. Clear it explicitly.
      msal.instance.setActiveAccount(null);
    }
  }
}
